import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Button, Input, Select, Form, message, Spin, Avatar,
  Divider, Tag, Radio, Space, Alert,
} from 'antd';
import {
  UserOutlined, SearchOutlined, CheckOutlined,
  ClockCircleOutlined, EnvironmentOutlined, ArrowLeftOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { FaUserMd, FaChevronLeft, FaChevronRight, FaUser, FaUserPlus, FaClock, FaInfoCircle } from 'react-icons/fa';
import { FaRegCalendarDays } from 'react-icons/fa6';
import { HiOutlineUser, HiOutlineMail } from 'react-icons/hi';
import { GiPhone, GiPadlock } from 'react-icons/gi';
import { MdLocalHospital } from 'react-icons/md';
import dayjs from 'dayjs';
import drEasyApi from '../../../Api/Customapi';
import { getImg } from '../../../Api/getImg';
import { useGetUserData } from '../../../hooks/useGetUserData';
import { useClinics } from '../../../hooks/useClinics';

// ─── Step indicator ───────────────────────────────────────────────────────────
const StepDot = ({ active, done, label, num }) => (
  <div className="flex flex-col items-center gap-1">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-300
      ${done ? 'bg-primary border-primary text-white' :
        active ? 'bg-white border-primary text-primary' :
          'bg-gray-100 border-gray-300 text-gray-400'}`}>
      {done ? <CheckOutlined style={{ fontSize: 12 }} /> : num}
    </div>
    <span className={`text-xs font-medium whitespace-nowrap ${active || done ? 'text-primary' : 'text-gray-400'}`}>
      {label}
    </span>
  </div>
);

const StepLine = ({ done }) => (
  <div className={`flex-1 h-0.5 mx-1 mt-4 mb-5 rounded transition-all duration-500 ${done ? 'bg-primary' : 'bg-gray-200'}`} />
);

// ─── STEP 0: Patient type choice ─────────────────────────────────────────────
const PatientTypeStep = ({ onSelect, isMobile }) => (
  <div className="py-4">
    <p className={`text-center text-gray-500 mb-6 ${isMobile ? 'text-sm' : ''}`}>
      Who would you like to book an appointment for?
    </p>
    <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'}`}>
      <button
        onClick={() => onSelect('existing')}
        className="group flex flex-col items-center justify-center p-6 rounded-2xl border-2 border-gray-200 hover:border-primary bg-white hover:bg-blue-50 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
      >
        <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary_hover rounded-full flex items-center justify-center mb-3 shadow-lg">
          <FaUser className="text-white text-2xl" />
        </div>
        <span className="font-bold text-gray-800 group-hover:text-primary transition-colors">Existing Patient</span>
        <span className="text-xs text-gray-500 mt-1 text-center">Select from registered patients</span>
      </button>
      <button
        onClick={() => onSelect('new')}
        className="group flex flex-col items-center justify-center p-6 rounded-2xl border-2 border-gray-200 hover:border-secondary bg-white hover:bg-green-50 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
      >
        <div className="w-14 h-14 bg-gradient-to-br from-secondary to-secondary_hover rounded-full flex items-center justify-center mb-3 shadow-lg">
          <FaUserPlus className="text-white text-2xl" />
        </div>
        <span className="font-bold text-gray-800 group-hover:text-secondary transition-colors">New Patient</span>
        <span className="text-xs text-gray-500 mt-1 text-center">Register and book in one flow</span>
      </button>
    </div>
  </div>
);

// ─── STEP 1a: Select existing patient ────────────────────────────────────────
const SelectExistingPatientStep = ({ onSelect, selectedPatient, isMobile }) => {
  const [search, setSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchPatients = useCallback(async (name = '') => {
    setLoading(true);
    try {
      const res = await drEasyApi.get('/api/Centers/getPatients', {
        params: { NameFilter: name || undefined, deactivated: false, Page: 1, PageSize: 20 },
      });
      setPatients(res.data?.patients || []);
      setTotal(res.data?.totalCount || 0);
    } catch {
      message.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);
  useEffect(() => {
    const t = setTimeout(() => fetchPatients(search), 350);
    return () => clearTimeout(t);
  }, [search, fetchPatients]);

  return (
    <div>
      <Input
        placeholder="Search patient by name…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        allowClear
        prefix={<SearchOutlined className="text-gray-400" />}
        size={isMobile ? 'middle' : 'large'}
        className="rounded-xl mb-3"
      />
      <div className="text-xs text-gray-500 mb-2">{total} patient(s) found</div>
      <Spin spinning={loading}>
        <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
          {patients.length === 0 && !loading && (
            <div className="text-center text-gray-400 py-8">No patients found</div>
          )}
          {patients.map(p => (
            <div
              key={p.id}
              onClick={() => onSelect(p)}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer border-2 transition-all duration-200
                ${selectedPatient?.id === p.id ? 'border-primary bg-blue-50 shadow-md' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}
            >
              <Avatar src={getImg(p.imageId)} size={40} style={{ backgroundColor: '#298E9F', flexShrink: 0 }}>
                {!getImg(p.imageId) && p.fullName?.charAt(0)?.toUpperCase()}
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-gray-800 truncate ${isMobile ? 'text-sm' : ''}`}>{p.fullName}</div>
                <div className={`text-gray-500 truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>{p.email}</div>
              </div>
              {selectedPatient?.id === p.id && (
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckOutlined className="text-white text-xs" />
                </div>
              )}
            </div>
          ))}
        </div>
      </Spin>
    </div>
  );
};

// ─── STEP 1b: Create new patient form ────────────────────────────────────────
const CreateNewPatientStep = ({ onCreated, isMobile }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { data } = useGetUserData();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        gender: values.gender,
        password: values.password,
        birthDay: `${values.dobYear}-${String(values.dobMonth).padStart(2, '0')}-${String(values.dobDay).padStart(2, '0')}`,
      };
      const res = await drEasyApi.post(`/api/${data?.slug}/User/RegisterAsCenterUser`, payload);
      message.success('Patient registered successfully!');
      onCreated({
        id: res.data?.id || res.data?.userId,
        accountId: res.data?.accountId || res.data?.id,
        fullName: `${values.firstName} ${values.lastName}`,
        email: values.email,
      });
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.message || err.response?.data?.errors?.[0] || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Form form={form} layout="vertical" size={isMobile ? 'middle' : 'large'}>
        <div className="grid grid-cols-2 gap-3">
          <Form.Item name="firstName" label="First Name" rules={[{ required: true }, { min: 2 }]} className="mb-3">
            <Input placeholder="First name" prefix={<HiOutlineUser />} />
          </Form.Item>
          <Form.Item name="lastName" label="Last Name" rules={[{ required: true }, { min: 2 }]} className="mb-3">
            <Input placeholder="Last name" prefix={<HiOutlineUser />} />
          </Form.Item>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email' }]} className="mb-3">
            <Input placeholder="Email" prefix={<HiOutlineMail />} />
          </Form.Item>
          <Form.Item name="phoneNumber" label="Phone" rules={[{ required: true }, { pattern: /^[\+]?[0-9][\d]{0,15}$/ }]} className="mb-3">
            <Input placeholder="Phone" prefix={<GiPhone />} />
          </Form.Item>
          <Form.Item name="gender" label="Gender" rules={[{ required: true }]} className="mb-3">
            <Select placeholder="Gender">
              <Select.Option value={0}>Male</Select.Option>
              <Select.Option value={1}>Female</Select.Option>
            </Select>
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Form.Item name="password" label="Password" rules={[{ required: true }, { min: 6 }, {
            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*#?&.]{6,}$/,
            message: 'Must contain uppercase, lowercase, and number',
          }]} className="mb-3">
            <Input.Password placeholder="Password" prefix={<GiPadlock />} />
          </Form.Item>
          <Form.Item name="confirmPassword" label="Confirm Password" dependencies={['password']}
            rules={[{ required: true }, ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) return Promise.resolve();
                return Promise.reject(new Error('Passwords do not match'));
              },
            })]} className="mb-3">
            <Input.Password placeholder="Confirm password" prefix={<GiPadlock />} />
          </Form.Item>
        </div>
        <div className="text-sm font-medium text-gray-700 mb-2">Date of Birth</div>
        <div className="grid grid-cols-3 gap-3">
          <Form.Item name="dobDay" label="Day" rules={[{ required: true }]} className="mb-0">
            <Select placeholder="Day">
              {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <Select.Option key={d} value={d}>{d}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="dobMonth" label="Month" rules={[{ required: true }]} className="mb-0">
            <Select placeholder="Month">
              {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                <Select.Option key={i + 1} value={i + 1}>{m}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="dobYear" label="Year" rules={[{ required: true }]} className="mb-0">
            <Select placeholder="Year" showSearch>
              {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <Select.Option key={y} value={y}>{y}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Form>
      <Button type="primary" block loading={loading} onClick={handleSubmit}
        className="mt-4 h-10 bg-primary hover:bg-primary_hover border-none rounded-xl font-semibold">
        {loading ? 'Registering…' : 'Register & Continue →'}
      </Button>
    </div>
  );
};

// ─── STEP 2: Select clinic ────────────────────────────────────────────────────
const ClinicSelectStep = ({ onSelect, selectedClinic, isMobile, onBack }) => {
  const [clinics, setClinics] = useState([]);
  const { clinicsQuery } = useClinics({
      pageNumber: 1,
      pageSize: 100,
      staleTime: Infinity,
    });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchClinics = async () => {
     setClinics(clinicsQuery?.data?.clinics)
    };
    fetchClinics();
  }, []);

  return (
    <div>
      <p className="text-gray-500 text-sm mb-4">Choose the clinic for this appointment</p>
      <Spin spinning={loading}>
        <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
          {clinics.length === 0 && !loading && (
            <div className="text-center text-gray-400 py-8">No clinics available</div>
          )}
          {clinics.map(c => (
            <div
              key={c.id || c.clinicId}
              onClick={() => onSelect(c)}
              className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer border-2 transition-all duration-200
                ${ selectedClinic?.clinicId === c.clinicId
                  ? 'border-primary bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary_hover flex items-center justify-center flex-shrink-0">
                <MdLocalHospital className="text-white text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-gray-800 ${isMobile ? 'text-sm' : ''}`}>
                  {c.name || c.clinicName}
                </div>
                {(c.detailedAddress || c.address) && (
                  <div className={`text-gray-500 truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    <EnvironmentOutlined className="mr-1" />
                    {c.detailedAddress || c.address}
                  </div>
                )}
                <div className="flex gap-2 mt-1">
                  {c.doctors?.length > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                      {c.doctors.length} doctor{c.doctors.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {(c.genralDays || c.generalDays)?.length > 0 && (
                    <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">
                      General slots
                    </span>
                  )}
                </div>
              </div>
              {(selectedClinic?.id === c.id || selectedClinic?.clinicId === c.clinicId) && (
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckOutlined className="text-white text-xs" />
                </div>
              )}
            </div>
          ))}
        </div>
      </Spin>
      <div className="flex gap-3 mt-4">
        <Button onClick={onBack} icon={<ArrowLeftOutlined />} className="rounded-xl flex-1">Back</Button>
        <Button
          type="primary"
          disabled={!selectedClinic}
          icon={<ArrowRightOutlined />}
          onClick={() => selectedClinic && onSelect(selectedClinic, true)}
          className="rounded-xl flex-1 bg-primary border-none"
          style={{ backgroundColor: selectedClinic ? '#298d9f' : undefined }}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

// ─── STEP 3: General vs specific doctor ──────────────────────────────────────
const AppointmentOptionStep = ({ selectedClinic, onSelect, isMobile, onBack }) => {
  const [selected, setSelected] = useState(null);

  const hasDoctors = (selectedClinic?.doctors?.length || 0) > 0;
  const hasGeneral = ((selectedClinic?.genralDays || selectedClinic?.generalDays)?.length || 0) > 0;

  const options = [
    hasGeneral && {
      key: 'general',
      icon: <FaClock className="text-xl" />,
      title: 'General Appointment',
      description: 'Book an available slot without a specific doctor',
      hint: 'Ideal for quick consultations or when any available doctor is fine.',
      color: 'blue',
    },
    hasDoctors && {
      key: 'specific',
      icon: <FaUserMd className="text-xl" />,
      title: 'Specific Doctor',
      description: 'Choose a particular doctor from this clinic',
      hint: 'Choose this if you prefer a specific doctors schedule and availability.',
      color: 'purple',
    },
  ].filter(Boolean);

  // Auto-select if only one option
  useEffect(() => {
    if (options.length === 1) {
      setSelected(options[0].key);
    }
  }, []);

  const activeHint = options.find(o => o.key === selected)?.hint;

  return (
    <div>
      <p className="text-gray-500 text-sm mb-4">How would you like to book this appointment?</p>

      {options.length === 0 && (
        <Alert message="This clinic has no available booking options configured." type="warning" showIcon />
      )}

      <div className={`grid ${options.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-3 mb-3`}>
        {options.map(opt => {
          const isSelected = selected === opt.key;
          const colorMap = {
            blue: { border: 'border-primary', bg: 'bg-blue-50', icon: 'bg-blue-100 text-primary', badge: 'bg-blue-100 text-primary' },
            purple: { border: 'border-violet-500', bg: 'bg-violet-50', icon: 'bg-violet-100 text-violet-600', badge: 'bg-violet-100 text-violet-600' },
          };
          const colors = colorMap[opt.color];
          return (
            <div
              key={opt.key}
              onClick={() => setSelected(opt.key)}
              className={`relative cursor-pointer rounded-xl p-4 bg-white transition-all duration-200
                ${isSelected ? `border-2 ${colors.border} ${colors.bg} shadow-sm` : 'border-2 border-gray-200 hover:border-gray-300'}`}
            >
              {isSelected && (
                <span className={`absolute top-2.5 right-2.5 text-[10px] font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>
                  Selected
                </span>
              )}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 transition-colors ${isSelected ? colors.icon : 'bg-gray-100 text-gray-400'}`}>
                {opt.icon}
              </div>
              <p className="text-sm font-semibold text-gray-800">{opt.title}</p>
              <p className="text-xs text-gray-500 mt-1">{opt.description}</p>
            </div>
          );
        })}
      </div>

      {activeHint && (
        <div className="flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2.5 mb-4">
          <FaInfoCircle className="text-primary text-sm shrink-0 mt-0.5" />
          <p className="text-xs text-gray-500">{activeHint}</p>
        </div>
      )}

      <div className="flex gap-3 mt-2">
        <Button onClick={onBack} icon={<ArrowLeftOutlined />} className="rounded-xl flex-1">Back</Button>
        <Button
          type="primary"
          disabled={!selected}
          icon={<ArrowRightOutlined />}
          onClick={() => selected && onSelect(selected)}
          className="rounded-xl flex-1 bg-primary border-none"
          style={{ backgroundColor: selected ? '#298d9f' : undefined }}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

// ─── STEP 4: Book appointment (date + time + confirm) ─────────────────────────
const BookAppointmentStep = ({ session, selectedClinic, appointmentOption, isMobile, onBooked, onBack }) => {
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [notes, setNotes] = useState('');
  const [notesError, setNotesError] = useState('');
  const [booking, setBooking] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const isSpecific = appointmentOption === 'specific';
  const doctors = selectedClinic?.doctors || [];
  const generalDays = selectedClinic?.genralDays || selectedClinic?.generalDays || [];

  const workingDays = [
    { value: 0, label: 'Sunday' }, { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' }, { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' }, { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  // Which working days to use for calendar
  const activeWorkingDays = isSpecific
    ? (selectedDoctor?.workingDays?.filter(w => w.isWorking) || [])
    : generalDays.filter(w => w.isWorking !== false);

  const disabledDate = (date) => {
    if (date < dayjs().startOf('day')) return true;
    return !activeWorkingDays.some(w => w.dayOfWeek === date.day());
  };

  // Find the working day entry for selected date
  const selectedWorkingDay = selectedDate
    ? activeWorkingDays.find(w => w.dayOfWeek === dayjs(selectedDate).day())
    : null;

  const availabilityHours = selectedWorkingDay?.availabilityHours || [];

  const generateHourlySlots = (startTime, endTime) => {
    const slots = [];
    let current = dayjs(`2000-01-01 ${startTime}`);
    const end = dayjs(`2000-01-01 ${endTime}`);
    while (current.isBefore(end)) {
      const next = current.add(1, 'hour');
      if (next.isAfter(end)) break;
      slots.push({ startTime: current.format('HH:mm'), endTime: next.format('HH:mm') });
      current = next;
    }
    return slots;
  };

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const num = parseInt(h, 10);
    const suffix = num >= 12 ? 'PM' : 'AM';
    return `${num % 12 || 12}:${m} ${suffix}`;
  };

  // Calendar grid
  const startOfMonth = currentMonth.startOf('month');
  const days = [];
  for (let i = 0; i < startOfMonth.day(); i++) days.push(null);
  for (let i = 1; i <= currentMonth.daysInMonth(); i++) days.push(startOfMonth.set('date', i));
  while (days.length % 7 !== 0) days.push(null);

  const handleDoctorSelect = (doc) => {
    setSelectedDoctor(selectedDoctor?.doctorId === doc.doctorId ? null : doc);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setSelectedHour(null);
  };

  const handleDateSelect = (day) => {
    setSelectedDate(day);
    setSelectedTimeSlot(null);
    setSelectedHour(null);
  };

  const handleBook = async () => {
    if (!notes.trim()) { setNotesError('Notes are required'); return; }
    if (!selectedTimeSlot || !selectedDate) { message.warning('Please complete all selections'); return; }
    if (selectedTimeSlot.isHourly && !selectedHour) { message.warning('Please select an hour'); return; }

    setBooking(true);
    try {
      await drEasyApi.post('/api/Book/Book', {
        availabilityHourId: selectedTimeSlot.availabilityHourId,
        bookingDate: dayjs(selectedDate).format('YYYY-MM-DD'),
        userId: session?.userId || session?.accountId || session?.patientId,
        notes,
        selectedHour: selectedTimeSlot.isHourly && selectedHour ? selectedHour.startTime : null,
      });
      message.success(`Booking created for ${session?.patientName || session?.fullName}`);
      onBooked();
    } catch (err) {
      message.error(err.response?.data?.[0] || 'Failed to create booking');
    } finally {
      setBooking(false);
    }
  };

  const showCalendar = isSpecific ? !!selectedDoctor : true;
  const canBook = selectedDate && selectedTimeSlot && (!selectedTimeSlot.isHourly || selectedHour);

  return (
    <div className="space-y-4">

      {/* Doctor selection (specific mode only) */}
      {isSpecific && (
        <div>
          <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <FaUserMd className="text-primary" /> Select Doctor
          </div>
          <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
            {doctors.length === 0 && (
              <div className="text-center text-gray-400 py-4 text-sm">No doctors in this clinic</div>
            )}
            {doctors.map(doc => (
              <div
                key={doc.doctorId}
                onClick={() => handleDoctorSelect(doc)}
                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer border-2 transition-all duration-200
                  ${selectedDoctor?.doctorId === doc.doctorId ? 'border-primary bg-blue-50 shadow-md' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary_hover flex items-center justify-center text-white flex-shrink-0 overflow-hidden">
                  {doc.photoId ? <img src={getImg(doc.photoId)} alt={doc.doctorName} className="w-full h-full object-cover" /> : <UserOutlined />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-800 text-sm truncate">Dr. {doc.doctorName}</div>
                  {doc.specialization && <div className="text-gray-500 text-xs truncate">{doc.specialization}</div>}
                </div>
                {selectedDoctor?.doctorId === doc.doctorId && (
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckOutlined className="text-white text-xs" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar */}
      {showCalendar && activeWorkingDays.length > 0 && (
        <>
          <Divider className="my-2" />
          <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <FaRegCalendarDays className="text-primary" /> Select Date
          </div>
          <div className="border border-gray-200 rounded-xl overflow-hidden max-w-xs mx-auto shadow-sm">
            <div className="flex justify-between items-center px-4 py-2.5 bg-primary">
              <Button type="text" icon={<FaChevronLeft className="text-white text-xs" />}
                onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))} className="text-white p-1 h-auto" />
              <span className="text-white font-semibold text-sm">{currentMonth.format('MMMM YYYY')}</span>
              <Button type="text" icon={<FaChevronRight className="text-white text-xs" />}
                onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))} className="text-white p-1 h-auto" />
            </div>
            <div className="grid grid-cols-7 bg-gray-50">
              {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d, i) => (
                <div key={i} className="text-xs font-medium text-gray-500 text-center py-1.5">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 bg-white">
              {days.map((day, i) => {
                if (!day) return <div key={i} className="h-9 bg-gray-50 border border-gray-100" />;
                const isSelected = selectedDate && dayjs(selectedDate).isSame(day, 'day');
                const disabled = disabledDate(day);
                return (
                  <div key={i}
                    className={`h-9 flex items-center justify-center border border-gray-100 transition-colors
                      ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:bg-blue-50 cursor-pointer'}`}
                    onClick={() => { if (!disabled) handleDateSelect(day); }}>
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs
                      ${isSelected ? 'bg-secondary text-white font-bold' : disabled ? 'text-gray-300' : 'text-gray-700'}`}>
                      {day.date()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Time slots */}
      {selectedDate && availabilityHours.length > 0 && (
        <>
          <Divider className="my-2" />
          <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <ClockCircleOutlined className="text-primary" /> Select Time Slot
          </div>
          <Radio.Group
            value={selectedTimeSlot?.availabilityHourId}
            onChange={e => {
              setSelectedTimeSlot(availabilityHours.find(h => h.availabilityHourId === e.target.value));
              setSelectedHour(null);
            }}
            className="w-full"
          >
            <Space direction="vertical" className="w-full">
              {availabilityHours.map(h => (
                <div key={h.availabilityHourId}
                  className={`p-3 rounded-xl border-2 transition-all duration-200 cursor-pointer
                    ${selectedTimeSlot?.availabilityHourId === h.availabilityHourId ? 'border-primary bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <Radio value={h.availabilityHourId} className="w-full">
                    <div className="flex items-center justify-between ml-2">
                      <div>
                        <div className="font-semibold text-gray-800 text-sm">
                          <ClockCircleOutlined className="mr-2" />{formatTime(h.startTime)} – {formatTime(h.endTime)}
                        </div>
                        <div className="text-gray-500 text-xs mt-0.5">
                          {h.isHourly ? 'Hourly Sessions' : 'Full Period'}
                          {h.maxPatients ? ` · Max ${h.maxPatients}` : ''}
                        </div>
                      </div>
                      <Tag color="green">Available</Tag>
                    </div>
                  </Radio>
                </div>
              ))}
            </Space>
          </Radio.Group>
        </>
      )}

      {/* Hourly slots */}
      {selectedTimeSlot?.isHourly && (
        <>
          <Divider className="my-2" />
          <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <FaClock className="text-primary" /> Select Hour
          </div>
          <div className="grid grid-cols-3 gap-2">
            {generateHourlySlots(selectedTimeSlot.startTime, selectedTimeSlot.endTime).map((h, i) => (
              <button key={i}
                onClick={() => setSelectedHour(h)}
                className={`py-2 px-3 rounded-lg text-xs font-medium border-2 transition-all duration-200
                  ${selectedHour?.startTime === h.startTime
                    ? 'bg-primary border-primary text-white shadow-sm'
                    : 'border-gray-200 text-gray-700 hover:border-primary hover:bg-blue-50'}`}>
                {formatTime(h.startTime)}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Notes */}
      {canBook && (
        <>
          <Divider className="my-2" />
          <div className="text-sm font-semibold text-gray-700 mb-2">📝 Notes</div>
          <Input.TextArea
            rows={2}
            value={notes}
            onChange={e => { setNotes(e.target.value); setNotesError(e.target.value ? '' : 'Notes are required'); }}
            placeholder="Add appointment notes…"
            className={`rounded-lg ${notesError ? 'border-red-400' : ''}`}
          />
          {notesError && <p className="text-red-500 text-xs mt-1">{notesError}</p>}
        </>
      )}

      {/* Summary */}
      {canBook && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-xl space-y-1 text-sm">
          <div className="font-semibold text-green-800">✅ Booking Summary</div>
          <div className="text-green-700">• <b>Patient:</b> {session?.patientName || session?.fullName}</div>
          <div className="text-green-700">• <b>Clinic:</b> {selectedClinic?.name || selectedClinic?.clinicName}</div>
          {isSpecific && selectedDoctor && (
            <div className="text-green-700">• <b>Doctor:</b> Dr. {selectedDoctor.doctorName}</div>
          )}
          {!isSpecific && <div className="text-green-700">• <b>Type:</b> General Appointment</div>}
          <div className="text-green-700">• <b>Date:</b> {dayjs(selectedDate).format('MMM D, YYYY')}</div>
          <div className="text-green-700">
            • <b>Time:</b>{' '}
            {selectedTimeSlot.isHourly && selectedHour
              ? `${formatTime(selectedHour.startTime)} – ${formatTime(selectedHour.endTime)}`
              : `${formatTime(selectedTimeSlot.startTime)} – ${formatTime(selectedTimeSlot.endTime)}`}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex gap-3 pt-1">
        <Button onClick={onBack} icon={<ArrowLeftOutlined />} className="rounded-xl flex-1">Back</Button>
        <Button
          type="primary"
          loading={booking}
          disabled={!canBook}
          onClick={handleBook}
          className="rounded-xl flex-1 bg-primary border-none"
          style={{ backgroundColor: canBook ? '#298d9f' : undefined }}
        >
          Confirm Booking
        </Button>
      </div>
    </div>
  );
};

// ─── MAIN MODAL ───────────────────────────────────────────────────────────────
const CreateBookingModal = ({ visible, onCancel, onSuccess, isMobile }) => {
  // flow states: 'choose' | 'existing-select' | 'new-register' | 'clinic-select' | 'option' | 'book'
  const [flow, setFlow] = useState('choose');
  const [patientType, setPatientType] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [appointmentOption, setAppointmentOption] = useState(null); // 'general' | 'specific'

  const handleReset = () => {
    setFlow('choose');
    setPatientType(null);
    setSelectedPatient(null);
    setSelectedClinic(null);
    setAppointmentOption(null);
  };

  const handleCancel = () => { handleReset(); onCancel(); };

  // Derive step index for stepper (shown after 'choose')
  const getStepIndex = () => {
    if (flow === 'existing-select' || flow === 'new-register') return 0;
    if (flow === 'clinic-select') return 1;
    if (flow === 'option') return 2;
    if (flow === 'book') return 3;
    return 0;
  };
  const stepIndex = getStepIndex();
  const steps = ['Patient', 'Clinic', 'Options', 'Book'];

  const titleMap = {
    choose: 'New Booking',
    'existing-select': 'Select Patient',
    'new-register': 'Register Patient',
    'clinic-select': 'Select Clinic',
    option: 'Appointment Type',
    book: 'Book Appointment',
  };

  const sessionData = selectedPatient ? {
    userId: selectedPatient.accountId || selectedPatient.id,
    patientId: selectedPatient.id,
    patientName: selectedPatient.fullName,
    fullName: selectedPatient.fullName,
    accountId: selectedPatient.accountId,
  } : null;

  return (
    <Modal
      title={<span className={isMobile ? 'text-sm' : ''}>{titleMap[flow]}</span>}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={isMobile ? '95vw' : 700}
      centered={isMobile}
      destroyOnClose
    >
      {/* Stepper */}
      {flow !== 'choose' && (
        <div className="flex items-start mb-5 px-1">
          {steps.map((label, idx) => (
            <React.Fragment key={idx}>
              <StepDot num={idx + 1} label={label} active={stepIndex === idx} done={stepIndex > idx} />
              {idx < steps.length - 1 && <StepLine done={stepIndex > idx} />}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Step 0: Choose patient type */}
      {flow === 'choose' && (
        <PatientTypeStep
          isMobile={isMobile}
          onSelect={type => {
            setPatientType(type);
            setFlow(type === 'existing' ? 'existing-select' : 'new-register');
          }}
        />
      )}

      {/* Step 1a: Select existing patient */}
      {flow === 'existing-select' && (
        <div>
          <SelectExistingPatientStep
            onSelect={p => setSelectedPatient(p)}
            selectedPatient={selectedPatient}
            isMobile={isMobile}
          />
          <div className="flex gap-3 mt-4">
            <Button onClick={() => { setFlow('choose'); setSelectedPatient(null); }} icon={<ArrowLeftOutlined />} className="rounded-xl flex-1">
              Back
            </Button>
            <Button
              type="primary"
              disabled={!selectedPatient}
              onClick={() => { if (selectedPatient) setFlow('clinic-select'); else message.warning('Please select a patient'); }}
              icon={<ArrowRightOutlined />}
              className="rounded-xl flex-1 bg-primary border-none"
              style={{ backgroundColor: selectedPatient ? '#298d9f' : undefined }}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Step 1b: Register new patient */}
      {flow === 'new-register' && (
        <div>
          <Button onClick={() => setFlow('choose')} icon={<ArrowLeftOutlined />} type="text" className="mb-3 text-gray-500 -ml-1">
            Back
          </Button>
          <CreateNewPatientStep
            isMobile={isMobile}
            onCreated={patient => { setSelectedPatient(patient); setFlow('clinic-select'); }}
          />
        </div>
      )}

      {/* Step 2: Select clinic */}
      {flow === 'clinic-select' && (
        <div>
          {/* Selected patient banner */}
          {selectedPatient && (
            <div className="flex items-center gap-3 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-xl">
              <Avatar size={32} style={{ backgroundColor: '#298E9F', flexShrink: 0 }}>
                {selectedPatient.fullName?.charAt(0)?.toUpperCase()}
              </Avatar>
              <div>
                <div className="font-semibold text-gray-800 text-sm">{selectedPatient.fullName}</div>
                <div className="text-xs text-gray-500">{selectedPatient.email}</div>
              </div>
            </div>
          )}
          <ClinicSelectStep
            isMobile={isMobile}
            selectedClinic={selectedClinic}
            onBack={() => setFlow(patientType === 'existing' ? 'existing-select' : 'new-register')}
            onSelect={(clinic, proceed) => {
              setSelectedClinic(clinic);
              setAppointmentOption(null);
              if (proceed) setFlow('option');
            }}
          />
        </div>
      )}

      {/* Step 3: General vs specific */}
      {flow === 'option' && selectedClinic && (
        <div>
          {selectedPatient && (
            <div className="flex items-center gap-3 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-xl">
              <Avatar size={32} style={{ backgroundColor: '#298E9F', flexShrink: 0 }}>
                {selectedPatient.fullName?.charAt(0)?.toUpperCase()}
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-800 text-sm">{selectedPatient.fullName}</div>
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <EnvironmentOutlined /> {selectedClinic?.name || selectedClinic?.clinicName}
                </div>
              </div>
            </div>
          )}
          <AppointmentOptionStep
            selectedClinic={selectedClinic}
            isMobile={isMobile}
            onBack={() => { setFlow('clinic-select'); setAppointmentOption(null); }}
            onSelect={option => { setAppointmentOption(option); setFlow('book'); }}
          />
        </div>
      )}

      {/* Step 4: Book */}
      {flow === 'book' && sessionData && selectedClinic && appointmentOption && (
        <div>
          {selectedPatient && (
            <div className="flex items-center gap-3 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-xl">
              <Avatar size={32} style={{ backgroundColor: '#298E9F', flexShrink: 0 }}>
                {selectedPatient.fullName?.charAt(0)?.toUpperCase()}
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-800 text-sm">{selectedPatient.fullName}</div>
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <EnvironmentOutlined /> {selectedClinic?.name || selectedClinic?.clinicName}
                  <span className="mx-1">·</span>
                  <span className={`capitalize font-medium ${appointmentOption === 'general' ? 'text-blue-600' : 'text-violet-600'}`}>
                    {appointmentOption === 'general' ? 'General' : 'Specific Doctor'}
                  </span>
                </div>
              </div>
            </div>
          )}
          <BookAppointmentStep
            session={sessionData}
            selectedClinic={selectedClinic}
            appointmentOption={appointmentOption}
            isMobile={isMobile}
            onBack={() => setFlow('option')}
            onBooked={() => {
              onSuccess?.({ patient: selectedPatient, clinic: selectedClinic, option: appointmentOption });
              handleReset();
              onCancel();
            }}
          />
        </div>
      )}
    </Modal>
  );
};

export default CreateBookingModal;